import React, { memo, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentCompanyId } from "hooks/auth";
import { InstructionsInfoBox } from "newui/components/ui/InstructionsInfoBox/InstructionsInfoBox";
import { getDocument } from "../api/insuranceVerificationApi";
import { insuranceVerificationKeys } from "../api/insuranceVerificationKeys";
import type { InsuranceOcrJobState } from "../types/InsuranceOcrJob.type";
import { en } from "../i18n/en";

const POLLING_CAP_MS = 120_000;
const ACTIVE_STATES: InsuranceOcrJobState[] = ["queued", "running"];

interface PendingVerificationBoxProps {
  documentId: number;
  initialState: InsuranceOcrJobState;
  /** Called when OCR transitions from an active state to a completed state */
  onComplete?: (newState: InsuranceOcrJobState) => void;
}

export const PendingVerificationBox: React.FC<PendingVerificationBoxProps> =
  memo(({ documentId, initialState, onComplete }) => {
    const companyId = useCurrentCompanyId() as number;
    const queryClient = useQueryClient();
    const [pollingEnabled, setPollingEnabled] = useState(
      ACTIVE_STATES.includes(initialState)
    );
    const completeFiredRef = useRef(false);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      if (!pollingEnabled) {
        return;
      }

      timeoutRef.current = setTimeout(() => {
        setPollingEnabled(false);
      }, POLLING_CAP_MS);

      return () => {
        if (timeoutRef.current !== null) {
          clearTimeout(timeoutRef.current);
        }
      };
    }, [pollingEnabled]);

    const { data } = useQuery({
      queryKey: insuranceVerificationKeys.document(companyId, documentId),
      queryFn: () => getDocument(companyId, documentId),
      select: (res) => res.data.data,
      enabled: pollingEnabled,
      refetchInterval: 5000,
      refetchIntervalInBackground: false,
    });

    const currentState = data?.latest_ocr_job?.state ?? data?.ocr_job?.state ?? initialState;

    useEffect(() => {
      if (!ACTIVE_STATES.includes(currentState)) {
        setPollingEnabled(false);

        if (!completeFiredRef.current && onComplete) {
          completeFiredRef.current = true;
          onComplete(currentState);
        }
      }
    }, [currentState, onComplete]);

    // Also invalidate the parent document list when complete
    useEffect(() => {
      if (!ACTIVE_STATES.includes(currentState) && completeFiredRef.current) {
        queryClient.invalidateQueries({
          queryKey: ["company", "documents"],
        });
      }
    }, [currentState, queryClient]);

    const isActive = ACTIVE_STATES.includes(currentState);

    return (
      <InstructionsInfoBox
        inline
        data-testid="pending-verification-box"
        title={isActive ? en.pending.title : "Verification Complete"}
      >
        {isActive
          ? "Processing your document. You can close this and come back — we'll update the status automatically."
          : "Verification finished. Close and reopen to review the results."}
      </InstructionsInfoBox>
    );
  });

PendingVerificationBox.displayName = "PendingVerificationBox";
